/**
 * step-launch.tsx — Bước 5: ra mắt. Gọi `GET /ai/bots/:id/status` → danh sách kiểm tra (nguồn AI · kênh đang kết nối ·
 * đã phát hành · AI tổ chức không tạm dừng · chế độ không phải thủ công · bật / trực tuyến…) với nút sửa theo `fix`.
 * "Bật trả lời khách": phát hành (nếu chưa / có thay đổi) → nếu tổ chức đang "Tạm dừng toàn bộ AI" thì hỏi rồi
 * `PUT /ai/config {autoReplyEnabled:true}` → bật nhân viên (+ trực tuyến). Xong: "Đang trả lời ở N kênh".
 * Nút sửa sang màn khác (Nguồn AI và lượt AI, Kết nối, màn chi tiết…) và "Xem hội thoại" mở TAB MỚI — bước này giữ nguyên,
 * "Kiểm tra lại" tải lại danh sách kiểm tra (cũng tự kiểm tra lại khi quay lại tab).
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, ArrowLeft, CheckCircle2, CircleDashed, Inbox, Loader2, Power, Settings2, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import { SOURCE_LABEL } from '@/lib/ai-credits'
import { AI_PATHS } from '@/lib/ai-khung'
import { useInboxAccounts } from '@/hooks/use-integrations'
import { useSetAiPausedAll } from '@/hooks/use-ai-khung'
import { OpenInNewTab } from '@/components/ai/open-in-new-tab'
import { ReloadButton } from '@/components/ai/reload-button'
import { publishWizardBot, updateWizardBot, useBotStatus, type WizardBot } from './wizard-api'
import {
  botDetailPath, fixStep, inboxForBotPath, launchBlocked, launchChecklist, launchFixPath, type CheckState, type LaunchCheck,
} from './wizard-lib'

function CheckIcon({ state }: { state: CheckState }) {
  const { t } = useTranslation('aiWizard')
  switch (state) {
    case 'ok': return <CheckCircle2 className="h-5 w-5 text-success" aria-label={t('launch.icon.ok')} />
    case 'fail': return <XCircle className="h-5 w-5 text-destructive" aria-label={t('launch.icon.fail')} />
    case 'auto': return <CircleDashed className="h-5 w-5 text-primary" aria-label={t('launch.icon.auto')} />
    case 'warn': return <AlertTriangle className="h-5 w-5 text-warning" aria-label={t('launch.icon.warn')} />
  }
}

export function StepLaunch({ bot, onChanged, onGoStep, onBack }: {
  bot: WizardBot
  onChanged: () => Promise<unknown>
  onGoStep: (n: number) => void
  onBack: () => void
}) {
  const { t } = useTranslation('aiWizard')
  const statusQ = useBotStatus(bot.id)
  const channelsQ = useInboxAccounts()
  const channels = channelsQ.data
  const setPaused = useSetAiPausedAll()
  const [busy, setBusy] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const status = statusQ.data
  const items = launchChecklist({ status, bot, channels: channels ?? [] })
  const blocked = launchBlocked(items)
  const orgPaused = items.some((c) => c.key === 'org' && c.state === 'auto')
  const live = bot.enabled && !!status?.answering
  const liveChannels = (status?.channels ?? []).filter((c) => c.connected !== false)

  async function launch(resume = false) {
    if (orgPaused && !resume) { setConfirmOpen(true); return }
    setConfirmOpen(false)
    setBusy(true)
    try {
      if (status?.publishedVersion == null || status.hasUnpublishedChanges) await publishWizardBot(bot.id)
      if (orgPaused) await setPaused.mutateAsync(false)
      await updateWizardBot(bot.id, { enabled: true, ...(bot.onlineStatus === 'offline' ? { onlineStatus: 'online' as const } : {}) })
      await onChanged()
      const s = await statusQ.refetch()
      if (s.data?.answering) toast.success(t('launch.started', { name: bot.name }))
      else toast.warning(t('launch.startedNotAnswering', { name: bot.name }))
    } catch (err) {
      toast.error(apiError(err))
      void statusQ.refetch()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-base font-medium">{t('launch.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('launch.intro', { name: bot.name })}</p>
      </div>

      {live ? (
        <section className="space-y-3 rounded-xl border border-success/40 bg-success/5 p-4">
          <p className="flex items-center gap-2 text-base font-medium text-success">
            <CheckCircle2 className="h-5 w-5" /> {t('launch.answeringAt', { count: liveChannels.length })}
          </p>
          {liveChannels.length > 0 && (
            <p className="text-sm text-muted-foreground">{liveChannels.map((c) => c.name || t('launch.unnamedChannel')).join(' · ')}</p>
          )}
          <div className="flex flex-wrap gap-2">
            <OpenInNewTab to={inboxForBotPath(bot.id)} variant="default" title={t('launch.openInboxTitle')}><Inbox className="h-4 w-4" /> {t('launch.viewConversations')}</OpenInNewTab>
            <Button variant="outline" asChild><Link to={botDetailPath(bot.id)}><Settings2 className="h-4 w-4" /> {t('launch.openDetail')}</Link></Button>
          </div>
        </section>
      ) : null}

      <section className="space-y-2 rounded-xl border p-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-medium">{t('launch.checklist')}</h3>
          <ReloadButton
            label={t('launch.recheck')} what={t('launch.checklistWhat')}
            onReload={() => Promise.all([onChanged(), statusQ.refetch(), channelsQ.refetch()])}
          />
        </div>
        {statusQ.isLoading ? (
          <p className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('launch.checking')}</p>
        ) : statusQ.isError ? (
          <p className="text-xs text-destructive">{t('launch.statusError', { error: apiError(statusQ.error) })}</p>
        ) : (
          <ul className="space-y-2">
            {items.map((c) => <CheckRow key={c.key} item={c} botId={bot.id} onGoStep={onGoStep} />)}
          </ul>
        )}
        {status?.effective?.model && (
          <p className="pt-1 text-[11px] text-muted-foreground">
            {status.effective.inherited ? t('launch.brainInherited') : t('launch.brain')}{status.effective.provider} · {status.effective.model}
            {status.effective.source ? ` · ${SOURCE_LABEL[status.effective.source] ?? status.effective.source}` : ''}
            {' — '}
            {status.effective.inherited
              ? <OpenInNewTab to={AI_PATHS.sources}>{t('launch.changeSource')}</OpenInNewTab>
              : <OpenInNewTab to={botDetailPath(bot.id, 'bo-nao')}>{t('launch.changeModel')}</OpenInNewTab>}
          </p>
        )}
      </section>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" onClick={onBack}><ArrowLeft className="h-4 w-4" /> {t('common:actions.back')}</Button>
        {!live && (
          <div className="flex items-center gap-2">
            {blocked && <span className="text-xs text-muted-foreground">{t('launch.fixFirst')}</span>}
            <Button onClick={() => void launch()} disabled={busy || blocked || statusQ.isLoading}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4" />} {t('launch.enable')}
            </Button>
          </div>
        )}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('launch.resumeTitle')}</DialogTitle>
            <DialogDescription>
              {t('launch.resumeDesc', { name: bot.name })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>{t('common:actions.cancel')}</Button>
            <Button onClick={() => void launch(true)}>{t('launch.resumeConfirm', { name: bot.name })}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function CheckRow({ item, botId, onGoStep }: { item: LaunchCheck; botId: string; onGoStep: (n: number) => void }) {
  const fix = item.fix
  const step = fix ? fixStep(fix.section) : null
  const path = fix ? launchFixPath(fix, botId) : null
  return (
    <li className={cn('flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2', item.state === 'fail' && 'border-destructive/40 bg-destructive/5')}>
      <CheckIcon state={item.state} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{item.label}</p>
        {item.detail && <p className="text-xs text-muted-foreground">{item.detail}</p>}
      </div>
      {fix && item.state !== 'ok' && (
        step ? (
          <Button size="sm" variant="outline" onClick={() => onGoStep(step)}>{fix.label}</Button>
        ) : path ? (
          <OpenInNewTab to={path} variant="outline" size="sm">{fix.label}</OpenInNewTab>
        ) : (
          <span className="text-xs text-muted-foreground">{fix.label}</span>
        )
      )}
    </li>
  )
}
